import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import {
  Clock,
  User,
  Shield,
  AlertTriangle,
  Check,
  X,
  FileText,
  ExternalLink,
  Copy,
} from "lucide-react";
import { GdprHistoryItem } from "../types/gdpr.types";
import "./GdprHistoryTimeline.css";

interface GdprHistoryTimelineProps {
  history: GdprHistoryItem[];
  isLoading?: boolean;
}

const ACTION_ICONS: Record<string, React.ReactNode> = {
  consent_requested: <FileText size={16} />,
  consent_given: <Check size={16} />,
  consent_withdrawn: <X size={16} />,
  data_deleted: <AlertTriangle size={16} />,
  external_consent_recorded: <Shield size={16} />,
};

const ACTION_LABELS: Record<string, string> = {
  consent_requested: "Consent Requested",
  consent_given: "Consent Given",
  consent_withdrawn: "Consent Withdrawn",
  data_deleted: "Data Deleted",
  external_consent_recorded: "External Consent Recorded",
};

const ACTION_COLORS: Record<string, string> = {
  consent_requested: "action-requested",
  consent_given: "action-given",
  consent_withdrawn: "action-withdrawn",
  data_deleted: "action-deleted",
  external_consent_recorded: "action-external",
};

const ACTOR_LABELS: Record<string, string> = {
  candidate: "Candidate",
  user: "User",
  system: "System",
};

const GdprHistoryTimeline: React.FC<GdprHistoryTimelineProps> = ({
  history,
  isLoading,
}) => {
  const { t } = useTranslation(["recruiting", "common"]);
  const [copiedTokenId, setCopiedTokenId] = useState<number | null>(null);

  const handleCopyConsentLink = async (token: string, itemId: number) => {
    const url = `${window.location.origin}/consent/${token}`;
    try {
      await navigator.clipboard.writeText(url);
      setCopiedTokenId(itemId);
      setTimeout(() => setCopiedTokenId(null), 2000);
    } catch (err) {
      console.error("Failed to copy:", err);
    }
  };
  if (isLoading) {
    return (
      <div className="gdpr-history-loading">
        <div className="spinner"></div>
        <p>{t("common:loading.loading")}</p>
      </div>
    );
  }

  if (!history || history.length === 0) {
    return (
      <div className="gdpr-history-empty">
        <Shield size={48} />
        <p>{t("common:empty.noData")}</p>
      </div>
    );
  }

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  const parseDetails = (
    details: string | undefined,
  ): Record<string, unknown> | null => {
    if (!details) return null;
    try {
      return JSON.parse(details);
    } catch {
      return null;
    }
  };

  return (
    <div className="gdpr-history-timeline">
      {history.map((item, index) => {
        const details = parseDetails(item.details);
        return (
          <div
            key={item.id}
            className={`timeline-item ${ACTION_COLORS[item.action_type] || "action-default"}`}
          >
            <div className="timeline-marker">
              <div className="marker-icon">
                {ACTION_ICONS[item.action_type] || <Shield size={16} />}
              </div>
              {index < history.length - 1 && (
                <div className="marker-line"></div>
              )}
            </div>
            <div className="timeline-content">
              <div className="timeline-header">
                <h4 className="action-title">
                  {ACTION_LABELS[item.action_type] || item.action_type}
                </h4>
                <span className="action-date">
                  <Clock size={14} /> {formatDate(item.created_at)}
                </span>
              </div>
              <div className="timeline-meta">
                <span className="actor-info">
                  <User size={14} />
                  {item.action_by_type === "user" && item.action_by_user_name
                    ? item.action_by_user_name
                    : ACTOR_LABELS[item.action_by_type] || item.action_by_type}
                </span>
                {item.ip_address && (
                  <span className="ip-address">IP: {item.ip_address}</span>
                )}
              </div>
              {details && (
                <div className="timeline-details">
                  {details.consent_type && (
                    <span className="detail-item">
                      <strong>{t("common:profile.type")}:</strong>{" "}
                      {details.consent_type as string}
                    </span>
                  )}
                  {details.reason && (
                    <span className="detail-item">
                      <strong>Reason:</strong> {details.reason as string}
                    </span>
                  )}
                  {details.notes && (
                    <span className="detail-item">
                      <strong>Notes:</strong> {details.notes as string}
                    </span>
                  )}
                </div>
              )}
              {item.consent_token &&
                item.action_type === "consent_requested" && (
                  <div className="timeline-consent-link">
                    <span className="consent-link-label">
                      {t("recruiting:candidates.detail.gdpr.title")}:
                    </span>
                    <div className="consent-link-actions">
                      <a
                        href={`${window.location.origin}/consent/${item.consent_token}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="consent-link-url"
                      >
                        <ExternalLink size={14} />
                        {t("common:actions.viewAll")}
                      </a>
                      <button
                        className="consent-link-copy-btn"
                        onClick={() =>
                          handleCopyConsentLink(item.consent_token!, item.id)
                        }
                      >
                        {copiedTokenId === item.id ? (
                          <>
                            <Check size={14} /> {t("common:status.success")}
                          </>
                        ) : (
                          <>
                            <Copy size={14} />{" "}
                            {t("common:actions.copy", { defaultValue: "Copy" })}
                          </>
                        )}
                      </button>
                    </div>
                  </div>
                )}
            </div>
          </div>
        );
      })}
    </div>
  );
};

export default GdprHistoryTimeline;
